import TabSwitcherWeb from "@components/switcher/TabSwitcherWeb";
import { primeServices } from "@services/apis/Prime";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getUserFeatureConfig } from "src/config/userFeatureConfig";
import { AppState } from "src/redux/reducer";
import ConstantFunctions from "../../utils/constants_functions";
import PrimeOrderFlow from "./PrimeOrderFlow";
import PrimeOrderFlowCanada from "./PrimeOrderFlowCanada";
import PrimeOrderFlowEurope from "./PrimeOrderFlowEurope";
import PrimeOrderFlowPhilippines from "./PrimeOrderFlowPhilippines";

const PrimeOrderFlowTabs = () => {
  const userId = useSelector((state: AppState) => state.user.userId);
  const userFeatureConfig = getUserFeatureConfig(userId);
  const constantFunctions = new ConstantFunctions();
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [ntxBalance, setNtxBalance] = useState<number | null>(null);
  const [ntxBalanceLoading, setNtxBalanceLoading] = useState(false);
  const [ntxBalanceReloadKey, setNtxBalanceReloadKey] = useState(0);

  const defaultTabs = [
    {
      label: "India",
      component: <PrimeOrderFlow key="IN" countryFilter="IN" />,
    },
    {
      label: "Brazil",
      component: <PrimeOrderFlow key="BR" countryFilter="BR" />,
    },
    {
      label: "Europe",
      component: <PrimeOrderFlowEurope />,
    },
    {
      label: "Philippines",
      component: <PrimeOrderFlowPhilippines />,
    },
    {
      label: "Canada",
      component: <PrimeOrderFlowCanada />,
    },
  ];
  const tabs = userFeatureConfig.prioritizePhilippinesTab
    ? [defaultTabs[3], ...defaultTabs.filter((_, i) => i !== 3)]
    : defaultTabs;

  const showNtxBrlBalance =
    tabs[activeTabIndex]?.label === "Brazil" &&
    (userId === 1246 || userId === 14016 || userId === 17500);

  useEffect(() => {
    if (!showNtxBrlBalance) return;

    let cancelled = false;
    let inFlight = false;

    const loadNtxBalance = async (silent = false) => {
      if (inFlight) return;
      inFlight = true;
      if (!silent) {
        setNtxBalanceLoading(true);
      }
      try {
        const response = await primeServices.getNtxBalance({ silent });
        if (!cancelled) {
          setNtxBalance(response.data.netAmount);
        }
      } catch {
        if (!cancelled && !silent) {
          setNtxBalance(null);
        }
      } finally {
        inFlight = false;
        if (!cancelled && !silent) {
          setNtxBalanceLoading(false);
        }
      }
    };

    loadNtxBalance();
    const intervalId = window.setInterval(() => {
      loadNtxBalance(true);
    }, 5000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [showNtxBrlBalance, ntxBalanceReloadKey]);

  return (
    <div className="flex flex-col h-full">
      <div className="h-[70px] border-b border-border-subtle bg-bg-page-body flex flex-row sticky top-0 bg-white z-10 pl-[20px]">
        <div className="flex flex-row justify-between items-center w-full mr-4 gap-4">
          <p className="text-text-base text-[14px] font-semibold shrink-0">
            Cross Ramp Order Flow
          </p>
          {showNtxBrlBalance && (
            <div className="flex items-center gap-1.5 border border-border-subtle rounded-md px-2 py-1 bg-bg-page-body shrink-0">
              <span className="text-text-subtle text-[11px] font-semibold whitespace-nowrap">
                BRL Balance:
              </span>
              <span className="text-text-base text-[11px] font-semibold whitespace-nowrap">
                {ntxBalanceLoading && ntxBalance === null
                  ? "..."
                  : ntxBalance === null
                    ? "--"
                    : constantFunctions.formatAmount(ntxBalance)}
              </span>
              <button
                type="button"
                title="Reload NTX BRL balance"
                aria-label="Reload NTX BRL balance"
                disabled={ntxBalanceLoading}
                onClick={() => setNtxBalanceReloadKey((key) => key + 1)}
                className="flex items-center justify-center disabled:opacity-50"
              >
                <span
                  className={`material-symbols-outlined text-[14px] leading-none ${ntxBalanceLoading ? "animate-spin" : ""
                    }`}
                >
                  refresh
                </span>
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-hidden mt-4 mx-4">
        <TabSwitcherWeb
          tabs={tabs}
          full={true}
          onTabChange={setActiveTabIndex}
        />
      </div>
    </div>
  );
};

export default PrimeOrderFlowTabs;
